import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, BookOpen, Clock, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow, SectionHeading } from "@/components/ui-bits";
import { publicApi } from "@/lib/api";
import { COMPANY } from "@/data/site";

const SITE = "https://eibattery.com.au";

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function Guides() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    publicApi
      .get("/guides", { params: category ? { category } : {} })
      .then((r) => {
        if (!live) return;
        setArticles(r.data.articles || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => live && setArticles([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [category]);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "EI Battery Guides", item: `${SITE}/guides` },
      ],
    }),
    []
  );

  return (
    <>
      <Seo
        title="EI Battery Guides — battery system guides for Australian installers"
        description="Practical guides for Australian solar installers, electricians and battery buyers: how battery power and capacity differ, what installers need before specifying a system, and what to check before ordering."
        canonical={`${SITE}/guides`}
        jsonLd={jsonLd}
      />

      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow className="text-ei-gold">EI Battery Guides</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
            Battery system guides for the Australian trade
          </h1>
          <p className="mt-5 text-base md:text-lg text-ei-white/75 max-w-2xl leading-relaxed">
            Plain-English guides for solar installers, electricians and battery buyers — written around the equipment
            we supply, so you can specify and order with confidence.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex flex-wrap gap-2" data-testid="guides-categories">
          <button
            onClick={() => setParams({})}
            data-testid="guides-category-all"
            className={`font-display text-xs font-bold px-4 py-2 rounded-sm border transition-colors min-h-[40px] ${
              !category ? "bg-ei-navy text-ei-white border-ei-navy" : "border-ei-mist text-ei-graphite hover:border-ei-navy"
            }`}
          >
            All guides
          </button>
          {categories.map((name) => (
            <button
              key={name}
              onClick={() => setParams({ category: name })}
              data-testid={`guides-category-${name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-display text-xs font-bold px-4 py-2 rounded-sm border transition-colors min-h-[40px] ${
                category === name ? "bg-ei-navy text-ei-white border-ei-navy" : "border-ei-mist text-ei-graphite hover:border-ei-navy"
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center" data-testid="guides-loading">
            <Loader2 className="w-6 h-6 animate-spin text-ei-navy/40" />
          </div>
        ) : articles.length === 0 ? (
          <div className="mt-10 border border-ei-mist rounded-md bg-white p-10 text-center" data-testid="guides-empty">
            <BookOpen className="w-6 h-6 mx-auto text-ei-golddark" />
            <p className="mt-3 text-sm text-ei-graphite/70">New guides are being prepared. Check back shortly.</p>
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-list">
            {articles.map((article) => (
              <article
                key={article.slug}
                className="group bg-white border border-ei-mist rounded-md overflow-hidden flex flex-col hover:border-ei-navy/40 transition-colors"
                data-testid={`guide-card-${article.slug}`}
              >
                {article.image && (
                  <Link to={`/guides/${article.slug}`} className="block aspect-[16/9] overflow-hidden bg-ei-mist/40">
                    <img
                      src={article.image}
                      alt={article.image_alt || article.title}
                      loading="lazy"
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </Link>
                )}
                <div className="p-5 flex flex-col flex-1">
                  {article.category && <Eyebrow className="text-ei-golddark">{article.category}</Eyebrow>}
                  <h2 className="mt-2 font-display text-base md:text-lg font-bold text-ei-navy leading-snug">
                    <Link to={`/guides/${article.slug}`} className="hover:text-ei-golddark transition-colors">
                      {article.title}
                    </Link>
                  </h2>
                  <p className="mt-2 text-sm text-ei-graphite/80 leading-relaxed flex-1">{article.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-ei-graphite/55">
                    <span>{fmtDate(article.published_at)}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {article.reading_minutes} min read
                    </span>
                  </div>
                  <Link
                    to={`/guides/${article.slug}`}
                    className="mt-4 inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy group-hover:gap-3 transition-all"
                    data-testid={`guide-read-${article.slug}`}
                  >
                    Read the guide <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-14 bg-ei-navy text-ei-white rounded-md p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold">Need a system specified for a job?</h2>
            <p className="mt-2 text-sm text-ei-white/70 max-w-xl leading-relaxed">
              Send us the site details and we'll come back with equipment, freight and lead times. Trade accounts only.
            </p>
          </div>
          <Link
            to="/request-a-quote"
            className="inline-flex items-center justify-center gap-2 bg-ei-gold text-ei-navy font-display text-sm font-bold px-6 py-3 rounded-sm hover:bg-ei-white transition-colors min-h-[48px]"
            data-testid="guides-enquiry-cta"
          >
            Request a quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="mt-4 text-xs text-ei-graphite/55">
          {COMPANY.name} supplies registered electrical contractors, builders and developers approved for a trade account.
        </p>
      </section>
    </>
  );
}
